import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import {
  pipe,
  pathOr,
  propEq,
  find,
  prop,
  isEmpty,
  reject,
  filter,
  map,
  evolve,
  flatten,
  anyPass
} from 'ramda'
import { Userpilot } from 'userpilot'
import { useDialogControllers } from '@machobear/design-system'
import ENV_VARS from '@/config'
import { useGetProduct, useGetUsages } from '@/hooks/use-block-and-pay'
import { TenantScreening, RentalAdvertising, LeaseaAndPayments } from '@/components/icons'
import useProfile from '@/hooks/use-profile'
import useHasCustomCompanyPage from '@/hooks/use-has-custom-company-page'
import formatCurrency from '@/utils/format-currency'
import useMyListings from '@/pages/Listing/MyListings/hooks/use-my-listings'
import useGetPlanKey from '@/hooks/use-get-plan-key'
import DialogWrapper from '../components/Dialogs/dialog-wrapper'

const { WEB_URL, APP_PATH, IMAGES_URL } = ENV_VARS

export type Product = {
  id: string
  title: string
  subtitle: string | React.ReactNode
  description: string
  icon: React.FunctionComponent
  to?: string
  onClick?: () => void
  disabled?: boolean
  actionText: string
  actionVariant: 'solid' | 'ghost'
  key: string
  tags: string[]
  detailsText?: string
  detailsOnClick?: () => void
  detailsDisabled?: boolean
}

type ProductsContextType = {
  products: Product[]
}

type ProductsProviderProps = {
  children: React.ReactNode
}

type Blocks = {
  company_id: string
}

const ProductsContext = createContext<ProductsContextType | undefined>(undefined)

export function useProductsContext() {
  const context = useContext(ProductsContext)
  if (!context) {
    throw new Error('useProductsContext must be used within a ProductsProvider')
  }
  return context
}

const getRatePerDay = (rate: string) => {
  const dayRate = Math.ceil((Number(rate) / 28) * 100) / 100
  return dayRate ? formatCurrency(dayRate, { hideCents: false }) : ''
}
const filterActiveListings = pipe(
  map(
    evolve({
      listings: filter(
        anyPass([propEq('state_machine', 'published'), propEq('state_machine', 'pending')])
      )
    })
  ),
  reject((building: any) => isEmpty(building.listings)),
  map(prop('listings')),
  flatten
)
const getListingLabel = (listing: {
  listing_id: string
  unit_number?: string
  address: string
}) => {
  let returnString = ''
  const unitText = `${listing.unit_number} - `
  if (listing?.unit_number) {
    returnString += unitText
  }
  return (returnString += `${listing?.address}`)
}

export function ProductsProvider({ children }: ProductsProviderProps) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const roomInfo = useSelector((state: any) => state.supportRoom.room_info)
  const roomId = roomInfo?.id
  const data = useProfile()
  const [selectedListing, setSelectedListing] = useState<{
    listingId: string
    address: string
  }>({ listingId: '', address: '' })
  const { data: sponsoredListingProduct, isLoading: isSponsoredListingProductLoading } =
    useGetProduct('thirty-days-sponsored-listing-package')

  const { data: creditCheckProduct, isLoading: isCreditCheckProductLoading } = useGetProduct(
    'credit-check-single-package'
  )

  const { listings: allMyListings, isLoading: isMyListingsLoading } = useMyListings()
  const { data: blockData, isLoading: isBlocksLoading }: { data: Blocks; isLoading: boolean } =
    useGetUsages()
  const activeListings = filterActiveListings(allMyListings || [])
  const handleSyndicationSubmit = (listingData: {
    listing_id: string
    unit_number: string
    address: string
  }) => {
    setSelectedListing({ listingId: listingData.listing_id, address: getListingLabel(listingData) })
    showModal('useSyndicationCredits')()
  }

  const isBillingUserValid = data?.billing_user_valid

  const { visible, showModal, closeModal } = useDialogControllers({
    sponsoredListingDialog: false,
    addendumsDialog: false,
    tenantScreeningDialog: false,
    warningDialog: false,
    advertiseYourListing: false,
    syndicationDialog: false,
    useSyndicationCredits: false,
    buyTenantScreening: false,
    buySponsoredListing: false,
    buySyndicationCredits: false,
    editPaymentDialog: false,
    billingUser: false,
    analyticsReport: false
  })
  const companyId = blockData?.company_id
  const hasCustomCompanyPage = useHasCustomCompanyPage(companyId)
  const handleDialogOpen = (id: Parameters<typeof showModal>[0]) =>
    isEmpty(activeListings) ? showModal('warningDialog') : showModal(id)

  const openCompanyPage = useCallback(() => {
    window.open(`${WEB_URL}/company/${companyId}`, '_blank')
  }, [companyId])

  const openPricingPage = useCallback(
    (id: string) => () => {
      Userpilot.track(id)
      window.open(`${WEB_URL}/pricing`, '_blank')
    },
    []
  )

  const openSponsoredListings = useCallback(() => {
    window.open(`${WEB_URL}/landlords/sponsored-listings`, '_blank')
  }, [])

  const sponsordListingPrice = pipe(
    pathOr([], ['products']),
    find(propEq('plan_name', 'thirty-days-sponsored-listing-package')),
    prop('value') as any,
    getRatePerDay
  )(sponsoredListingProduct)

  const priceData = pipe(
    pathOr([], ['products']),
    find(propEq('plan_name', 'credit-check-single-package')),
    prop('value')
  )(creditCheckProduct)

  const creditCheckPrice = priceData ? formatCurrency(priceData, { hideCents: false }) : '$16.99'

  const currentPlan = data?.company_info?.base_plan_name as keyof typeof Plans

  const planText = useGetPlanKey()

  const handleSendMessage = async (message: string) => {
    await dispatch.supportRoom.sendMessage({ message })
    window.open(`${APP_PATH}/chat/support-room`, '_blank')
  }

  const handleSendAnalytics = () => {
    handleSendMessage(
      `Hi there,
      I am interested in unlocking a monthly analytics report.
      If you could please provide more information and next steps, I would greatly appreciate it.`
    )
  }
  const handleSendCompanyPages = () => {
    handleSendMessage(
      `Hi there,
      I am interested in unlocking a branded company landing page.
      If you could please provide more information and next steps, I would greatly appreciate it.`
    )
  }
  const tenantScreening: Product = {
    id: `usage-credit-${planText}-tenant-screen-rpt-buy-credits-btn-click`,
    title: t('l.credit_usage.product.tenant_screening.title', 'Tenant Screening Report'),
    subtitle: (
      <Trans
        i18nKey="l.credit_usage.product.tenant_screening_dynamic.subtitle"
        creditCheckPrice={creditCheckPrice}
      >
        Starting at {{ creditCheckPrice }} / report
      </Trans>
    ),
    description: t(
      'l.credit_usage.product.tenant_screening.description',
      'Get complete information about applicants in seconds, including Equifax® credit information, verification documents, court records, and more.'
    ),
    icon: TenantScreening,
    onClick: isBillingUserValid ? showModal('buyTenantScreening') : showModal('billingUser'),
    actionText: t('l.credit_usage.product.tenant_screening.action', 'Buy credits'),
    actionVariant: 'solid',
    key: `tenant-screening-${planText}`,
    tags: ['tenant-screening']
  }

  const sponsoredListing: Product = {
    id: `usage-credit-${planText}-sl-buy-credits-btn-click`,
    title: t('common.sponsored_listings', 'Sponsored Listings'),
    subtitle: (
      <Trans
        i18nKey="l.credit_usage.product.sposored_listing.subtitle"
        sponsordListingPrice={sponsordListingPrice}
      >
        Starting at {{ sponsordListingPrice: sponsordListingPrice || '$1.79' }} / day
      </Trans>
    ),
    description: t(
      'l.credit_usage.product.sposored_listing.description',
      'Get up to 3X more inquiries by boosting your listing to the top of renters search results.'
    ),
    icon: RentalAdvertising,
    onClick: isBillingUserValid ? showModal('buySponsoredListing') : showModal('billingUser'),
    actionText: t('l.credit_usage.product.sponsored_listing.action', 'Buy credits'),
    actionVariant: 'solid',
    key: `sponsored-listings-${planText}`,
    tags: ['rental-advertising'],
    detailsText: t('common.learn_more', 'Learn more'),
    detailsOnClick: openSponsoredListings
  }

  const syndication: Product = {
    id: 'usage-credit-multiplatform-adv-contact-sales-btn-click',
    title: t('l.credit_usage.product.syndication.title', 'Multi-Platform Advertising'),
    subtitle: t('l.credit_usage.product.syndication.subtitle', 'Starting at $6.99 / platform'),
    description: t(
      'l.credit_usage.product.syndication.text',
      'Maximize your reach with automated sharing to popular platforms like Facebook, Craigslist, and Zumper.'
    ),
    icon: RentalAdvertising,
    onClick: isBillingUserValid ? showModal('buySyndicationCredits') : showModal('billingUser'),
    actionText: t('l.credit_usage.product.sponsored_listing.action', 'Buy credits'),
    actionVariant: 'solid',
    key: `syndication-${planText}`,
    tags: ['rental-advertising'],
    detailsText: t('common.learn_more', 'Learn more'),
    detailsOnClick: showModal('advertiseYourListing')
  }
  const ESSENTIALS_PRODUCTS: Product[] = [
    tenantScreening,
    sponsoredListing,
    syndication,
    {
      id: 'usage-credit-monthly-analytics-rpt-upgrade-plan-btn-click',
      title: t('l.credit_usage.product.analytics.title', 'Monthly Analytics Report'),
      subtitle: t('l.credit_usage.product.analytics.subtitle', 'Business plans only (Add-on)'),
      description: t(
        'l.credit_usage.product.analytics.description',
        'Get access to monthly analytics reports for up to 100 syndicated listings and keep track of how many clicks, views, and inquiries your listings received.'
      ),
      icon: RentalAdvertising,
      onClick: openPricingPage('app_upgrade_rentaladv_rpt_btn'),
      actionText: t('l.credit_usage.product.analytics.action', 'Upgrade plan'),
      actionVariant: 'solid',
      key: 'analytics-ESSENTIALS',
      tags: ['rental-advertising'],
      detailsText: t('common.view_example', 'View example'),
      detailsOnClick: () => {
        window.open(`${IMAGES_URL}/extras/liv.rent_Analytics_Report_Example.pdf`, '_blank')
      }
    },
    {
      id: 'usage-credit-co-landing-page-standard-upgrade-plan-btn-click',
      title: t(
        'l.credit_usage.product.landing_page_standard.title',
        'Company Landing Page (Standard)'
      ),
      subtitle: t('l.credit_usage.product.landing_page.subtitle', 'Growth & Business plans only'),
      description: t(
        'l.credit_usage.product.landing_page.description',
        'Highlight your listing portfolio with a branded corporate landing page.'
      ),
      icon: RentalAdvertising,
      onClick: openPricingPage('app_upgrade_rentaladv_co_lp_standard_btn'),
      actionText: t('l.credit_usage.product.landing_page.action', 'Upgrade plan'),
      actionVariant: 'solid',
      key: 'landing-page-ESSENTIALS',
      tags: ['rental-advertising']
    },
    {
      id: 'usage-credit-co-landing-page-custom-upgrade-plan-btn-click',
      title: t('l.credit_usage.product.landing_page_custom.title', 'Company Landing Page (Custom)'),
      subtitle: t('l.credit_usage.product.business_plans.text', 'Business plans only'),
      description: t(
        'l.credit_usage.product.custom_landing_page.description',
        'Take your listing portfolio to the next level with full customization options.'
      ),
      icon: RentalAdvertising,
      onClick: openPricingPage('app_upgrade_rentaladv_co_lp_custom_btn'),
      actionText: t('l.credit_usage.product.addendums.action', 'Upgrade plan'),
      actionVariant: 'solid',
      key: 'landing-page-custom-ESSENTIALS',
      tags: ['rental-advertising']
    },
    {
      id: 'usage-credit-livrent-addendum-upgrade-plan-btn-click',
      title: t('l.credit_usage.product.addendums.title', 'Addendums by liv.rent'),
      subtitle: t('l.credit_usage.product.addendums.subtitle', 'Growth & Business plans only'),
      description: t(
        'l.credit_usage.product.addendums.description',
        'Bulletproof your lease agreements with a variety of preset, lawyer-vetted addendums that can be added to your contracts in seconds.'
      ),
      icon: LeaseaAndPayments,
      onClick: openPricingPage('app_upgrade_leasepay_livrent_addendum_btn'),
      actionText: t('l.credit_usage.product.addendums.action', 'Upgrade plan'),
      actionVariant: 'solid',
      key: 'addendums-ESSENTIALS',
      tags: ['lease-payments']
    },
    {
      id: 'usage-credit-onetime-addendum-upgrade-plan-btn-click',
      title: t('l.credit_usage.product.onetime_addendums.title', 'One-time Addendums'),
      subtitle: t(
        'l.credit_usage.product.onetime_addendums.subtitle',
        'Growth & Business plans only'
      ),
      description: t(
        'l.credit_usage.product.onetime_addendums.description',
        'Addendums created for your personal use - this won’t be saved or shared with your team.'
      ),
      icon: LeaseaAndPayments,
      onClick: openPricingPage('app_upgrade_leasepay_onetime_addendum_btn'),
      actionText: t('l.credit_usage.product.onetime_addendums.action', 'Upgrade plan'),
      actionVariant: 'solid',
      key: 'onetime-addendums-ESSENTIALS',
      tags: ['lease-payments']
    },
    {
      id: 'usage-credit-custom-addendum-upgrade-plan-btn-click',
      title: t('l.credit_usage.product.custom_addendums.title', 'Custom Addendums'),
      subtitle: t('l.credit_usage.product.custom_addendums.subtitle', 'Business plans only'),
      description: t(
        'l.credit_usage.product.custom_addendums.description',
        'Upload and integrate your company’s addendums for easy signing and storage directly on the liv.rent platform.'
      ),
      icon: LeaseaAndPayments,
      onClick: openPricingPage('app_upgrade_leasepay_custom_addendum_btn'),
      actionText: t('l.credit_usage.product.custom_addendums.action', 'Upgrade plan'),
      actionVariant: 'solid',
      key: 'custom-addendums-ESSENTIALS',
      tags: ['lease-payments']
    }
  ]

  const GROWTH_PRODUCTS: Product[] = [
    tenantScreening,
    sponsoredListing,
    syndication,
    {
      id: 'usage-credit-growth-monthly-analytics-rpt-upgrade-plan-btn-click',
      title: t('l.credit_usage.product.analytics.title', 'Monthly Analytics Report'),
      subtitle: t('l.credit_usage.product.analytics.subtitle', 'Business plans only (Add-on)'),
      description: t(
        'l.credit_usage.product.analytics.description',
        'Get access to monthly analytics reports for up to 100 syndicated listings and keep track of how many clicks, views, and inquiries your listings received.'
      ),
      icon: RentalAdvertising,
      onClick: openPricingPage('app_upgrade_rentaladv_rpt_btn'),
      actionText: t('l.credit_usage.product.analytics.action', 'Upgrade plan'),
      actionVariant: 'solid',
      key: 'analytics-GROWTH',
      tags: ['rental-advertising'],
      detailsText: t('common.view_example', 'View example'),
      detailsOnClick: () => {
        window.open(`${IMAGES_URL}/extras/liv.rent_Analytics_Report_Example.pdf`, '_blank')
      }
    },
    {
      id: 'usage-credit-growth-co-landing-page-standard-view-page-btn-click',
      title: t(
        'l.credit_usage.product.landing_page_standard.title',
        'Company Landing Page (Standard)'
      ),
      subtitle: t('l.credit_usage.product.landing_page.subtitle', 'Growth & Business plans only'),
      description: t(
        'l.credit_usage.product.landing_page.description',
        'Highlight your listing portfolio with a branded corporate landing page.'
      ),
      icon: RentalAdvertising,
      onClick: openCompanyPage,
      actionText: t('l.credit_usage.product.view_page.action', 'View page'),
      actionVariant: 'ghost',
      key: 'landing-page-GROWTH',
      tags: ['rental-advertising']
    },
    {
      id: 'usage-credit-growth-co-landing-page-custom-upgrade-plan-btn-click',
      title: t('l.credit_usage.product.landing_page_custom.title', 'Company Landing Page (Custom)'),
      subtitle: t('l.credit_usage.product.business_plans.text', 'Business plans only'),
      description: t(
        'l.credit_usage.product.custom_landing_page.description',
        'Take your listing portfolio to the next level with full customization options.'
      ),
      icon: RentalAdvertising,
      onClick: openPricingPage('app_upgrade_rentaladv_co_lp_custom_btn'),
      actionText: t('l.credit_usage.product.addendums.action', 'Upgrade plan'),
      actionVariant: 'solid',
      key: 'landing-page-GROWTH',
      tags: ['rental-advertising']
    },
    {
      id: 'usage-credit-growth-livrent-addendum-get-started-btn-click',
      title: t('l.credit_usage.product.addendums.title', 'Addendums by liv.rent'),
      subtitle: t('l.credit_usage.product.addendums.subtitle', 'Growth & Business plans only'),
      description: t(
        'l.credit_usage.product.addendums.description',
        'Bulletproof your lease agreements with a variety of preset, lawyer-vetted addendums that can be added to your contracts in seconds.'
      ),
      icon: LeaseaAndPayments,
      onClick: handleDialogOpen('addendumsDialog'),
      actionText: t('l.credit_usage.product.get_started.action', 'Get started'),
      actionVariant: 'ghost',
      key: 'addendums-GROWTH',
      tags: ['lease-payments']
    },
    {
      id: 'usage-credit-growth-onetime-addendum-get-started-btn-click',
      title: t('l.credit_usage.product.onetime_addendums.title', 'One-time Addendums'),
      subtitle: t(
        'l.credit_usage.product.onetime_addendums.subtitle',
        'Growth & Business plans only'
      ),
      description: t(
        'l.credit_usage.product.onetime_addendums.description',
        'Addendums created for your personal use - this won’t be saved or shared with your team.'
      ),
      icon: LeaseaAndPayments,
      onClick: handleDialogOpen('addendumsDialog'),
      actionText: t('l.credit_usage.product.get_started.action', 'Get started'),
      actionVariant: 'ghost',
      key: 'onetime-addendums-GROWTH',
      tags: ['lease-payments']
    },
    {
      id: 'usage-credit-growth-custom-addendum-upgrade-plan-btn-click',
      title: t('l.credit_usage.product.custom_addendums.title', 'Custom Addendums'),
      subtitle: t('l.credit_usage.product.custom_addendums.subtitle', 'Business plans only'),
      description: t(
        'l.credit_usage.product.custom_addendums.description',
        'Upload and integrate your company’s addendums for easy signing and storage directly on the liv.rent platform.'
      ),
      icon: LeaseaAndPayments,
      onClick: openPricingPage('app_upgrade_leasepay_custom_addendum_btn'),
      actionText: t('l.credit_usage.product.custom_addendums.action', 'Upgrade plan'),
      actionVariant: 'solid',
      key: 'custom-addendums-GROWTH',
      tags: ['lease-payments']
    }
  ]
  const BUSINESS_PRODUCTS: Product[] = [
    tenantScreening,
    sponsoredListing,
    syndication,
    {
      id: 'usage-credit-business-monthly-analytics-rpt-contact-sales-btn-click',
      title: t('l.credit_usage.product.analytics.title', 'Monthly Analytics Report'),
      subtitle: t('l.credit_usage.product.analytics.subtitle', 'Business plans only (Add-on)'),
      description: t(
        'l.credit_usage.product.analytics.description',
        'Get access to monthly analytics reports for up to 100 syndicated listings and keep track of how many clicks, views, and inquiries your listings received.'
      ),
      icon: RentalAdvertising,
      onClick: handleSendAnalytics,
      actionText: t('l.credit_usage.product.syndication.action', 'Contact sales'),
      actionVariant: 'ghost',
      key: 'analytics-BUSINESS',
      tags: ['rental-advertising'],
      detailsText: t('common.view_example', 'View example'),
      detailsOnClick: () => {
        window.open(`${IMAGES_URL}/extras/liv.rent_Analytics_Report_Example.pdf`, '_blank')
      }
    },
    {
      id: 'usage-credit-business-co-landing-page-standard-view-page-btn-click',
      title: t(
        'l.credit_usage.product.landing_page_standard.title',
        'Company Landing Page (Standard)'
      ),
      subtitle: t('l.credit_usage.product.landing_page.subtitle', 'Growth & Business plans only'),
      description: t(
        'l.credit_usage.product.landing_page.description',
        'Highlight your listing portfolio with a branded corporate landing page.'
      ),
      icon: RentalAdvertising,
      onClick: openCompanyPage,
      disabled: hasCustomCompanyPage,
      actionText: t('l.credit_usage.product.view_page.action', 'View page'),
      actionVariant: 'ghost',
      key: 'landing-page-BUSINESS',
      tags: ['rental-advertising']
    },
    {
      id: hasCustomCompanyPage
        ? 'usage-credit-business-co-landing-page-custom-view-page-btn-click'
        : 'usage-credit-business-co-landing-page-custom-contact-sales-btn-click',
      title: t('l.credit_usage.product.landing_page_custom.title', 'Company Landing Page (Custom)'),
      subtitle: t('l.credit_usage.product.business_plans.text', 'Business plans only'),
      description: t(
        'l.credit_usage.product.custom_landing_page.description',
        'Take your listing portfolio to the next level with full customization options.'
      ),
      icon: RentalAdvertising,
      onClick: hasCustomCompanyPage ? openCompanyPage : handleSendCompanyPages,
      actionText: hasCustomCompanyPage
        ? t('l.credit_usage.product.view_page.action', 'View page')
        : t('l.credit_usage.product.syndication.action', 'Contact sales'),
      actionVariant: 'ghost',
      key: 'landing-page-custom-BUSINESS',
      tags: ['rental-advertising']
    },
    {
      id: 'usage-credit-business-livrent-addendum-get-started-btn-click',
      title: t('l.credit_usage.product.addendums.title', 'Addendums by liv.rent'),
      subtitle: t('l.credit_usage.product.addendums.subtitle', 'Growth & Business plans only'),
      description: t(
        'l.credit_usage.product.addendums.description',
        'Bulletproof your lease agreements with a variety of preset, lawyer-vetted addendums that can be added to your contracts in seconds.'
      ),
      icon: LeaseaAndPayments,
      onClick: handleDialogOpen('addendumsDialog'),
      actionText: t('l.credit_usage.product.get_started.action', 'Get started'),
      actionVariant: 'ghost',
      key: 'addendums-BUSINESS',
      tags: ['lease-payments']
    },
    {
      id: 'usage-credit-business-onetime-addendum-get-started-btn-click',
      title: t('l.credit_usage.product.onetime_addendums.title', 'One-time Addendums'),
      subtitle: t(
        'l.credit_usage.product.onetime_addendums.subtitle',
        'Growth & Business plans only'
      ),
      description: t(
        'l.credit_usage.product.onetime_addendums.description',
        'Addendums created for your personal use - this won’t be saved or shared with your team.'
      ),
      icon: LeaseaAndPayments,
      onClick: handleDialogOpen('addendumsDialog'),
      actionText: t('l.credit_usage.product.get_started.action', 'Get started'),
      actionVariant: 'ghost',
      key: 'onetime-addendums-BUSINESS',
      tags: ['lease-payments']
    },
    {
      id: 'usage-credit-business-custom-addendum-get-started-btn-click',
      title: t('l.credit_usage.product.custom_addendums.title', 'Custom Addendums'),
      subtitle: t('l.credit_usage.product.custom_addendums.subtitle', 'Business plans only'),
      description: t(
        'l.credit_usage.product.custom_addendums.description',
        'Upload and integrate your company’s addendums for easy signing and storage directly on the liv.rent platform.'
      ),
      icon: LeaseaAndPayments,
      onClick: handleDialogOpen('addendumsDialog'),
      actionText: t('l.credit_usage.product.get_started.action', 'Get started'),
      actionVariant: 'ghost',
      key: 'custom-addendums-BUSINESS',
      tags: ['lease-payments']
    }
  ]
  const Plans = {
    'essentials-plan-monthly': ESSENTIALS_PRODUCTS,
    'growth-plan-monthly': GROWTH_PRODUCTS,
    'growth-plan-annual': GROWTH_PRODUCTS,
    'business-plan-annual': BUSINESS_PRODUCTS,
    'business-plan-monthly': BUSINESS_PRODUCTS,
    'business-plan-special': BUSINESS_PRODUCTS,
    'growth-plan-monthly-coupon-wbrothers': GROWTH_PRODUCTS
  }

  const productsState =
    currentPlan &&
    !isMyListingsLoading &&
    !isSponsoredListingProductLoading &&
    !isCreditCheckProductLoading &&
    !isBlocksLoading
      ? Plans[currentPlan]
      : []

  useEffect(() => {
    const getSupportRoom = async () => {
      await dispatch.supportRoom.getSupportRoom()
    }
    if (!roomId) {
      getSupportRoom()
    }
  }, [dispatch.supportRoom, roomId])

  const value = {
    products: productsState
  }

  return (
    <ProductsContext.Provider value={value}>
      <>
        {children}
        <DialogWrapper
          visible={visible}
          closeModal={closeModal}
          showModal={showModal}
          selectedListing={selectedListing}
          handleSyndicationSubmit={handleSyndicationSubmit}
        />
      </>
    </ProductsContext.Provider>
  )
}

export default ProductsContext
